/**
 * Industry Context Provider
 *
 * Provides domain-agnostic configuration throughout the application.
 * Supports dynamic industry switching and terminology translation.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  IndustryConfig,
  IndustryContextValue,
  IndustryId,
  IndustryTerminology,
  AssetTypeConfig,
  KPIDomainConfig,
  MaintenanceTypeConfig,
  ComplianceStandardConfig,
  ProfessionalRole,
  RoleCategory,
  ExplanationStyle,
  PartsCatalog,
  Part,
  RootCauseLibrary,
  RootCauseDefinition,
  RootCausePattern,
  LaborRateStructure,
  IndustryPhysicsContext,
  IndustryKPIContext,
  IndustryNarrative,
  WhyThisMatters,
} from '../config/industryTypes'
import {
  industryRegistry,
  availableIndustries,
  defaultIndustryId,
  getIndustryConfig,
  loadDynamicIndustries,
  getAvailableIndustries,
} from '../config/industries'
import {
  ModuleConfig,
  ModuleId,
  SubPageConfig,
  ModuleWorkflow,
} from '../config/modules/moduleTypes'
import {
  getModulesForIndustry,
  getModuleName,
  getSubPagesForModule,
  getSubPageLabel,
  getWorkflowsForModule,
  isModuleEnabled,
  getModuleNavItems,
} from '../config/modules/index'

// =============================================================================
// CONTEXT
// =============================================================================

const IndustryContext = createContext<IndustryContextValue | null>(null)

// =============================================================================
// STORAGE KEYS
// =============================================================================

const INDUSTRY_STORAGE_KEY = 'ryedore-industry'

// =============================================================================
// PROVIDER COMPONENT
// =============================================================================

interface IndustryProviderProps {
  children: React.ReactNode
  defaultIndustry?: IndustryId
  /**
   * Industry from user's organization (takes precedence when set)
   * This is typically passed from AuthContext.industry
   */
  organizationIndustry?: string | null
}

export function IndustryProvider({ children, defaultIndustry, organizationIndustry }: IndustryProviderProps) {
  // 2026-05-24: Hub vendor users need to switch industries freely
  // (one Hub serves all 18). Spoke users are locked to their license's
  // INDUSTRY_ID via organizationIndustry. Priority changes per mode:
  //   - Spoke: organization > localStorage > default (org wins,
  //     enforcing the license)
  //   - Hub: localStorage > organization > default (user choice wins
  //     so the IndustrySelector picker is authoritative across reloads)
  // VITE_BUILD_MODE is set at build time by vite.config.ts (hub|spoke).
  // We use this instead of useDeploymentMode() because that hook is
  // async; we need a synchronous answer here for the useState
  // initializer below.
  const isHubMode = import.meta.env.VITE_BUILD_MODE !== 'spoke'
  // i18next 'common' translator — the IndustryContext `t` below delegates
  // generic UI keys (title_*, label_*, etc.) here when they aren't industry
  // terminology, so they don't fall through to the raw-key title-caser
  // (which produced visible "Title_kpi_domains" etc.). useTranslation makes
  // it reactive to language switches.
  const { t: i18nT } = useTranslation('common')
  const [industryId, setIndustryId] = useState<IndustryId>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(INDUSTRY_STORAGE_KEY)
      if (isHubMode && stored) {
        // Hub: user's last selection beats org default
        return stored as IndustryId
      }
      if (organizationIndustry) {
        return organizationIndustry as IndustryId
      }
      if (stored) {
        return stored as IndustryId
      }
    } else if (organizationIndustry) {
      return organizationIndustry as IndustryId
    }
    return defaultIndustry || defaultIndustryId
  })

  const prevOrgIndustryRef = useRef<string | null>(null)

  useEffect(() => {
    // Spoke: re-sync to org on change. Hub: don't override — the user's
    // explicit selection via IndustrySelector is authoritative.
    if (isHubMode) return
    if (organizationIndustry && organizationIndustry !== prevOrgIndustryRef.current) {
      prevOrgIndustryRef.current = organizationIndustry
      setIndustryId(organizationIndustry as IndustryId)
    }
  }, [organizationIndustry, isHubMode])

  const [isLoading, setIsLoading] = useState(false)
  const [dynamicLoaded, setDynamicLoaded] = useState(false)
  // Track which industries we've already attempted to hydrate to prevent loops
  const hydratedRef = useRef<Set<string>>(new Set())
  const [hydrationVersion, setHydrationVersion] = useState(0)

  // Load dynamic industries from API on mount
  useEffect(() => {
    if (!dynamicLoaded) {
      loadDynamicIndustries().then(() => setDynamicLoaded(true)).catch(() => setDynamicLoaded(true))
    }
  }, [dynamicLoaded])

  // If the current industry isn't in the static registry, ask the backend
  // to build a full frontend config for it via IndustryAdapterService.
  // Triggers when login switches to e.g. share_market.
  useEffect(() => {
    if (!industryId || hydratedRef.current.has(industryId)) return
    if (industryId in industryRegistry) return  // static, no hydration needed
    hydratedRef.current.add(industryId)
    import('@/config/industries').then(mod => {
      mod.hydrateIndustryFromBackend(industryId).then(success => {
        if (success) setHydrationVersion(v => v + 1)
      })
    }).catch(() => undefined)
  }, [industryId, dynamicLoaded])

  // Get current industry config — re-read on hydration so dynamic
  // industries (e.g. share_market) refresh after the backend round-trip.
  const industry = useMemo(
    () => getIndustryConfig(industryId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [industryId, hydrationVersion]
  )

  // Persist industry selection
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(INDUSTRY_STORAGE_KEY, industryId)
    }
  }, [industryId])

  // ==========================================================================
  // INDUSTRY SWITCHING
  // ==========================================================================

  const setIndustry = useCallback((id: IndustryId) => {
    setIsLoading(true)
    // Simulate async loading (in real app, might fetch config from API)
    setTimeout(() => {
      setIndustryId(id)
      setIsLoading(false)
    }, 100)
  }, [])

  // ==========================================================================
  // TERMINOLOGY HELPER
  // ==========================================================================

  // Generic English fallback so unknown industries (e.g. share_market when
  // dynamic config returns an empty terminology dict) still render usable
  // labels instead of literal key names like "asset" / "failure".
  // Mirrors the GENERIC_TERMINOLOGY in v2_server/services/industry_adapter.py.
  const GENERIC_TERMINOLOGY_FALLBACK: Record<string, string> = {
    asset: 'Item',
    assets: 'Items',
    assetPlural: 'Items',
    assetType: 'Item Type',
    failure: 'Anomaly Event',
    health: 'Status',
    healthScore: 'Status Score',
    healthy: 'Healthy',
    degraded: 'Degraded',
    critical: 'Critical',
    alert: 'Alert',
    process: 'Workflow',
    maintenance: 'Service',
    inspection: 'Review',
    downtime: 'Disruption',
    workOrder: 'Task',
    prediction: 'Forecast',
    anomaly: 'Anomaly',
    trend: 'Trend',
    risk: 'Risk',
    facility: 'Site',
  }

  const t = useCallback(
    (key: keyof IndustryTerminology | string): string => {
      const fromIndustry = (industry.terminology as any)?.[key]
      if (fromIndustry) return fromIndustry
      // Fall through to English fallback (NOT the literal key name)
      const fromFallback = GENERIC_TERMINOLOGY_FALLBACK[String(key)]
      if (fromFallback) return fromFallback
      // Generic UI labels (title_*, label_*, …) live in the i18next 'common'
      // namespace, not the industry terminology config. Delegate before the
      // title-case last resort so they render real (localized) text instead
      // of the literal key. i18next echoes the key on a miss → only accept a
      // value that actually differs from the key.
      const fromI18n = i18nT(String(key))
      if (fromI18n && fromI18n !== String(key)) return fromI18n
      // Last resort: title-case the key so it at least looks like a word
      return String(key)
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, c => c.toUpperCase())
        .trim()
    },
    [industry, i18nT]
  )

  // ==========================================================================
  // ASSET TYPE HELPERS
  // ==========================================================================

  // 2026-07-02 — coalesce to [] for minimal/stub-config industries (e.g.
  // wearable_etd, aquaculture stubs). These arrays are undefined on stub
  // configs; consumers do `getX().length`/`.map()` at render and crash the
  // whole page into the ErrorBoundary otherwise. Guard at the source so ALL
  // consumers (CognitiveTwin, PredictionAudit, ComparativeIndustrial, …) are safe.
  const getAssetTypes = useCallback((): AssetTypeConfig[] => {
    return Array.isArray(industry.assetTypes) ? industry.assetTypes : []
  }, [industry])

  const getAssetType = useCallback(
    (id: string): AssetTypeConfig | undefined => {
      return (Array.isArray(industry.assetTypes) ? industry.assetTypes : []).find(at => at.id === id)
    },
    [industry]
  )

  // ==========================================================================
  // KPI DOMAIN HELPERS
  // ==========================================================================

  const getKPIDomains = useCallback((): KPIDomainConfig[] => {
    return Array.isArray(industry.kpiDomains) ? industry.kpiDomains : []
  }, [industry])

  const getKPIDomain = useCallback(
    (id: string): KPIDomainConfig | undefined => {
      return (Array.isArray(industry.kpiDomains) ? industry.kpiDomains : []).find(d => d.id === id)
    },
    [industry]
  )

  // ==========================================================================
  // MAINTENANCE HELPERS
  // ==========================================================================

  const getMaintenanceTypes = useCallback((): MaintenanceTypeConfig[] => {
    return Array.isArray(industry.maintenanceTypes) ? industry.maintenanceTypes : []
  }, [industry])

  const getMaintenanceType = useCallback(
    (id: string): MaintenanceTypeConfig | undefined => {
      return (Array.isArray(industry.maintenanceTypes) ? industry.maintenanceTypes : []).find(m => m.id === id)
    },
    [industry]
  )

  // ==========================================================================
  // COMPLIANCE HELPERS
  // ==========================================================================

  const getComplianceStandards = useCallback((): ComplianceStandardConfig[] => {
    return Array.isArray(industry.complianceStandards) ? industry.complianceStandards : []
  }, [industry])

  // ==========================================================================
  // PROFESSIONAL ROLE HELPERS
  // ==========================================================================

  const getProfessionalRoles = useCallback((): ProfessionalRole[] => {
    return industry.professionalRoles || []
  }, [industry])

  const getRolesByCategory = useCallback(
    (category: RoleCategory): ProfessionalRole[] => {
      return (industry.professionalRoles || []).filter(role => role.category === category)
    },
    [industry]
  )

  const getProfessionalRole = useCallback(
    (id: string): ProfessionalRole | undefined => {
      return (industry.professionalRoles || []).find(role => role.id === id)
    },
    [industry]
  )

  const getExplanationStyle = useCallback(
    (roleId: string): ExplanationStyle | undefined => {
      const role = (industry.professionalRoles || []).find(r => r.id === roleId)
      return role?.explanationStyle
    },
    [industry]
  )

  // ==========================================================================
  // PARTS CATALOG HELPERS
  // ==========================================================================

  const getPartsCatalog = useCallback((): PartsCatalog => {
    return industry.partsCatalog || { categories: [] }
  }, [industry])

  const getPart = useCallback(
    (partId: string): Part | undefined => {
      const catalog = industry.partsCatalog
      if (!catalog) return undefined
      for (const category of catalog.categories) {
        const part = category.parts.find(p => p.id === partId)
        if (part) return part
      }
      return undefined
    },
    [industry]
  )

  const getPartsByAssetType = useCallback(
    (assetTypeId: string): Part[] => {
      const catalog = industry.partsCatalog
      if (!catalog) return []
      const parts: Part[] = []
      for (const category of catalog.categories) {
        for (const part of category.parts) {
          if (part.compatibleAssetTypes.includes(assetTypeId)) {
            parts.push(part)
          }
        }
      }
      return parts
    },
    [industry]
  )

  const getPartsByFailureMode = useCallback(
    (failureModeId: string): Part[] => {
      const catalog = industry.partsCatalog
      if (!catalog) return []
      const parts: Part[] = []
      for (const category of catalog.categories) {
        for (const part of category.parts) {
          if ((part.failureModes || []).includes(failureModeId)) {
            parts.push(part)
          }
        }
      }
      return parts
    },
    [industry]
  )

  // ==========================================================================
  // ROOT CAUSE HELPERS
  // ==========================================================================

  const getRootCauseLibrary = useCallback((): RootCauseLibrary => {
    return industry.rootCauseLibrary || { categories: [], commonPatterns: [] }
  }, [industry])

  const getRootCause = useCallback(
    (id: string): RootCauseDefinition | undefined => {
      const library = industry.rootCauseLibrary
      if (!library) return undefined
      for (const category of library.categories) {
        const rootCause = (category.rootCauses || []).find(rc => rc.id === id)
        if (rootCause) return rootCause
      }
      return undefined
    },
    [industry]
  )

  const getRootCausesByCategory = useCallback(
    (categoryId: string): RootCauseDefinition[] => {
      const library = industry.rootCauseLibrary
      if (!library) return []
      const category = library.categories.find(c => c.id === categoryId)
      return category?.rootCauses || []
    },
    [industry]
  )

  const getRootCausePatterns = useCallback((): RootCausePattern[] => {
    return industry.rootCauseLibrary?.commonPatterns || []
  }, [industry])

  // ==========================================================================
  // LABOR RATE HELPERS
  // ==========================================================================

  const getLaborRates = useCallback((): LaborRateStructure => {
    return industry.laborRates || { roles: [], overtimeMultiplier: 1.5, emergencyMultiplier: 2.0, weekendMultiplier: 1.5 }
  }, [industry])

  const getLaborRate = useCallback(
    (roleId: string): number => {
      const rates = industry.laborRates
      if (!rates) return 0
      const role = rates.roles.find(r => r.roleId === roleId)
      return role?.hourlyRate || 0
    },
    [industry]
  )

  // ==========================================================================
  // MODULE HELPERS
  // ==========================================================================

  const getEnabledModules = useCallback((): ModuleConfig[] => {
    return getModulesForIndustry(industryId)
  }, [industryId])

  const getModuleDisplayName = useCallback(
    (moduleId: string): string => {
      return getModuleName(moduleId as ModuleId, industryId)
    },
    [industryId]
  )

  const getModuleSubPages = useCallback(
    (moduleId: string): SubPageConfig[] => {
      return getSubPagesForModule(moduleId as ModuleId, industryId)
    },
    [industryId]
  )

  const getModuleSubPageLabel = useCallback(
    (moduleId: string, subPageId: string): string => {
      return getSubPageLabel(moduleId as ModuleId, subPageId, industryId)
    },
    [industryId]
  )

  const getModuleWorkflows = useCallback(
    (moduleId: string): ModuleWorkflow[] => {
      return getWorkflowsForModule(moduleId as ModuleId, industryId)
    },
    [industryId]
  )

  const checkModuleEnabled = useCallback(
    (moduleId: string): boolean => {
      return isModuleEnabled(moduleId as ModuleId, industryId)
    },
    [industryId]
  )

  const getNavItems = useCallback(() => {
    return getModuleNavItems(industryId)
  }, [industryId])

  // ==========================================================================
  // NARRATIVE GENERATION
  // ==========================================================================

  const generateNarrative = useCallback(
    (templateId: string, data: Record<string, unknown>): string => {
      const template = industry.narrativeTemplates.find(t => t.id === templateId)
      if (!template) {
        return ''
      }

      let narrative = template.template

      // Replace terminology placeholders
      for (const variable of template.variables) {
        const placeholder = `{${variable.name}}`

        if (variable.type === 'terminology') {
          // Replace with industry-specific terminology
          const termKey = variable.name as keyof IndustryTerminology
          if (termKey in industry.terminology) {
            narrative = narrative.replace(placeholder, industry.terminology[termKey] || String(termKey))
          }
        } else if (variable.type === 'currency') {
          // Format as currency
          const value = data[variable.name]
          if (typeof value === 'number') {
            narrative = narrative.replace(
              placeholder,
              new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }).format(value)
            )
          }
        } else if (variable.type === 'percentage') {
          // Format as percentage
          const value = data[variable.name]
          if (typeof value === 'number') {
            narrative = narrative.replace(placeholder, `${value.toFixed(1)}`)
          }
        } else {
          // Direct replacement
          const value = data[variable.name]
          if (value !== undefined) {
            narrative = narrative.replace(placeholder, String(value))
          }
        }
      }

      // Clean up any remaining placeholders
      narrative = narrative.replace(/\{[^}]+\}/g, '')

      return narrative
    },
    [industry]
  )

  // ==========================================================================
  // ROLE-BASED EXPLANATION GENERATION
  // ==========================================================================

  const generateRoleBasedExplanation = useCallback(
    (
      roleId: string,
      context: 'health' | 'anomaly' | 'prediction' | 'maintenance' | 'failure' | 'prediction_audit' | 'reasoning_analysis' | 'causal_analysis' | 'experiment' | string,
      data: Record<string, unknown>
    ): string => {
      const roles = industry.professionalRoles || []
      // Accept either a literal role id ("reliability_engineer") or a role
      // category ("sme", "executive", "operations") — when the literal id misses,
      // fall back to the first role tagged with that category. Audit/reasoning
      // pages call this with the category so the copy doesn't depend on which
      // specific SME persona an industry config happens to define.
      const role =
        roles.find((r) => r.id === roleId) ||
        roles.find((r) => r.category === (roleId as typeof r.category))

      // Brain-context explanations (audit / reasoning / causal / experiment)
      // are written from industry terminology only — they do not consume the
      // role's explanationStyle. Render them even when no matching role is
      // configured so the page never shows "No explanation available".
      const isBrainContext =
        context === 'prediction_audit' ||
        context === 'reasoning_analysis' ||
        context === 'causal_analysis' ||
        context === 'experiment'

      if (!role && !isBrainContext) {
        return 'No explanation available for this role.'
      }

      const style = role?.explanationStyle ?? {
        depth: 'technical' as const,
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical' as const,
        focusOn: ['technical_details', 'action_items'] as const,
      }
      const assetTerm = industry.terminology.asset
      const assetName = data.assetName as string || 'this asset'
      const healthScore = data.healthScore as number
      const anomalyType = data.anomalyType as string
      const predictionType = data.predictionType as string
      const maintenanceType = data.maintenanceType as string
      const failureMode = data.failureMode as string
      const cost = data.estimatedCost as number
      const timeToFailure = data.timeToFailure as string
      const rootCause = data.rootCause as string
      const recommendation = data.recommendation as string

      let explanation = ''

      // Generate context-appropriate explanation based on role
      switch (context) {
        case 'health':
          if (style.vocabularyLevel === 'executive') {
            explanation = `${assetTerm} "${assetName}" is currently at ${healthScore}% health.`
            if (style.includeCosts && cost) {
              explanation += ` Potential financial impact: $${cost.toLocaleString()}.`
            }
            if (style.includeTimelines && timeToFailure) {
              explanation += ` Estimated time to intervention: ${timeToFailure}.`
            }
            if (style.focusOn.includes('business_impact')) {
              explanation += ' This affects overall production capacity and reliability targets.'
            }
          } else if (style.vocabularyLevel === 'technical') {
            explanation = `${assetTerm} "${assetName}" health score: ${healthScore}%.`
            if (style.includeRootCause && rootCause) {
              explanation += ` Primary concern: ${rootCause}.`
            }
            if (style.includeProcedures && recommendation) {
              explanation += ` Recommended action: ${recommendation}.`
            }
            if (style.focusOn.includes('technical_details')) {
              explanation += ` Review sensor data and trend analysis for detailed diagnostics.`
            }
          } else {
            explanation = `${assetTerm} "${assetName}" is operating at ${healthScore}% health.`
            if (style.includeMetrics) {
              explanation += ` Key metrics are being monitored.`
            }
            if (style.includeRootCause && rootCause) {
              explanation += ` Watch for: ${rootCause}.`
            }
          }
          break

        case 'anomaly':
          if (style.vocabularyLevel === 'executive') {
            explanation = `An ${anomalyType || 'operational'} anomaly has been detected on ${assetTerm} "${assetName}".`
            if (style.focusOn.includes('risk')) {
              explanation += ' Risk assessment is being conducted.'
            }
            if (style.includeCosts && cost) {
              explanation += ` Potential cost exposure: $${cost.toLocaleString()}.`
            }
          } else if (style.vocabularyLevel === 'technical') {
            explanation = `Anomaly detected: ${anomalyType || 'deviation from baseline'} on ${assetTerm} "${assetName}".`
            if (style.includeRootCause && rootCause) {
              explanation += ` Suspected cause: ${rootCause}.`
            }
            if (style.includeProcedures) {
              explanation += ` Diagnostic steps: Check sensor readings, verify calibration, inspect for physical damage.`
            }
          } else {
            explanation = `Anomaly alert for ${assetTerm} "${assetName}": ${anomalyType || 'abnormal behavior detected'}.`
            if (style.includeMetrics) {
              explanation += ` Compare current readings against normal operating range.`
            }
          }
          break

        case 'prediction':
          if (style.vocabularyLevel === 'executive') {
            explanation = `AI prediction for ${assetTerm} "${assetName}": ${predictionType || 'potential issue identified'}.`
            if (style.includeTimelines && timeToFailure) {
              explanation += ` Timeline: ${timeToFailure}.`
            }
            if (style.includeCosts && cost) {
              explanation += ` Budget impact: $${cost.toLocaleString()}.`
            }
            if (style.focusOn.includes('business_impact')) {
              explanation += ' Plan proactive intervention to avoid unplanned downtime.'
            }
          } else if (style.vocabularyLevel === 'technical') {
            explanation = `Predictive alert: ${predictionType || 'degradation trend'} detected on ${assetTerm} "${assetName}".`
            if (style.includeRootCause && rootCause) {
              explanation += ` Root cause analysis points to: ${rootCause}.`
            }
            if (style.includeProcedures && recommendation) {
              explanation += ` Procedure: ${recommendation}.`
            }
            if (style.includeTimelines && timeToFailure) {
              explanation += ` Window for action: ${timeToFailure}.`
            }
          } else {
            explanation = `Prediction for ${assetTerm} "${assetName}": ${predictionType || 'maintenance may be needed soon'}.`
            if (style.includeTimelines && timeToFailure) {
              explanation += ` Timeframe: ${timeToFailure}.`
            }
          }
          break

        case 'maintenance':
          if (style.vocabularyLevel === 'executive') {
            explanation = `${maintenanceType || 'Maintenance'} scheduled for ${assetTerm} "${assetName}".`
            if (style.includeCosts && cost) {
              explanation += ` Estimated cost: $${cost.toLocaleString()}.`
            }
            if (style.focusOn.includes('business_impact')) {
              explanation += ' Production schedule has been coordinated.'
            }
          } else if (style.vocabularyLevel === 'technical') {
            explanation = `${maintenanceType || 'Maintenance task'} required for ${assetTerm} "${assetName}".`
            if (style.includeProcedures && recommendation) {
              explanation += ` Steps: ${recommendation}.`
            }
            if (style.includeRootCause && rootCause) {
              explanation += ` Addressing: ${rootCause}.`
            }
            explanation += ' Ensure proper LOTO procedures and PPE requirements.'
          } else {
            explanation = `${maintenanceType || 'Scheduled maintenance'} for ${assetTerm} "${assetName}".`
            if (style.includeTimelines) {
              explanation += ' Check work order for timeline and resource requirements.'
            }
          }
          break

        case 'failure':
          if (style.vocabularyLevel === 'executive') {
            explanation = `${industry.terminology.failure} on ${assetTerm} "${assetName}": ${failureMode || 'equipment down'}.`
            if (style.includeCosts && cost) {
              explanation += ` Impact: $${cost.toLocaleString()}.`
            }
            if (style.focusOn.includes('risk')) {
              explanation += ' Risk mitigation and recovery plan in progress.'
            }
          } else if (style.vocabularyLevel === 'technical') {
            explanation = `${industry.terminology.failure} event: ${failureMode || 'critical failure'} on ${assetTerm} "${assetName}".`
            if (style.includeRootCause && rootCause) {
              explanation += ` Root cause: ${rootCause}.`
            }
            if (style.includeProcedures && recommendation) {
              explanation += ` Recovery procedure: ${recommendation}.`
            }
            explanation += ' Document findings for RCA.'
          } else {
            explanation = `${industry.terminology.failure} reported: ${failureMode || 'equipment issue'} on ${assetTerm} "${assetName}".`
            if (style.includeRootCause && rootCause) {
              explanation += ` Likely cause: ${rootCause}.`
            }
          }
          break

        // Brain Intelligence context types
        case 'prediction_audit':
          explanation = `This ${t('prediction')} audit validates every AI decision through ${industry.terminology.compliance || 'compliance'} checks, `
            + `confidence calibration, and chain-of-evidence tracking for ${t('asset')} "${assetName || 'your equipment'}". `
            + `Each prediction is verified against ${industry.terminology.process || 'operational'} standards before action.`
          break

        case 'reasoning_analysis':
          explanation = `The reasoning chain shows how the AI reached its ${t('prediction')} for ${t('asset')} "${assetName || 'this equipment'}". `
            + `Each step (deductive, causal, probabilistic) is validated against ${industry.terminology.compliance || 'industry'} standards `
            + `and physics constraints specific to your ${t('facility') || 'facility'}.`
          break

        case 'causal_analysis':
          explanation = `Causal analysis maps the root-cause relationships between ${t('process')} variables for ${t('asset')} "${assetName || 'your equipment'}". `
            + `Understanding causal structure (not just correlation) enables targeted ${t('maintenance')} interventions `
            + `that address the true source of ${t('failure')} risk.`
          break

        case 'experiment':
        case 'experiment_lab':
          explanation = `The AI Experiment Lab runs controlled simulations for ${t('asset')} "${assetName || 'your equipment'}" `
            + `using physics-validated digital twins, 6-agent specialist analysis, and ${industry.terminology.compliance || 'compliance'} checks. `
            + `Results include statistical significance (p-values), SHAP explanations, and actionable ${t('maintenance')} recommendations.`
          break

        case 'fairness_assessment':
          explanation = `AI Fairness monitors prediction equity across all ${t('asset')} groups in your ${t('facility')}. `
            + `Statistical parity, equal opportunity, and disparate impact metrics ensure no ${t('asset')} type `
            + `receives systematically biased ${t('prediction')}s or ${t('maintenance')} recommendations.`
          break

        default:
          // For any unmapped context, generate a generic but industry-specific explanation
          explanation = `${t('asset')} "${assetName || 'your equipment'}" analysis using ${industry.terminology.process || 'operational'} `
            + `data and ${industry.terminology.compliance || 'industry'} standards.`
          break
      }

      return explanation
    },
    [industry]
  )

  // ==========================================================================
  // BRAIN INTELLIGENCE CONTEXT HELPERS (backend-sourced physics/KPI/narrative)
  // ==========================================================================

  // Per-industry caches; survive until industry changes.
  const physicsCacheRef = useRef<Record<string, IndustryPhysicsContext>>({})
  const kpiCacheRef = useRef<Record<string, IndustryKPIContext>>({})
  const narrativeCacheRef = useRef<Record<string, IndustryNarrative>>({})

  // Clear caches when industry switches
  useEffect(() => {
    physicsCacheRef.current = {}
    kpiCacheRef.current = {}
    narrativeCacheRef.current = {}
  }, [industryId])

  const getPhysicsContext = useCallback(
    async (): Promise<IndustryPhysicsContext> => {
      const cached = physicsCacheRef.current[industryId]
      if (cached) return cached
      const res = await fetch(`/api/v2/context/physics/${industryId}`)
      if (!res.ok) {
        throw new Error(`Physics context fetch failed: ${res.status}`)
      }
      const data: IndustryPhysicsContext = await res.json()
      physicsCacheRef.current[industryId] = data
      return data
    },
    [industryId]
  )

  const getKPIContext = useCallback(
    async (): Promise<IndustryKPIContext> => {
      const cached = kpiCacheRef.current[industryId]
      if (cached) return cached
      const res = await fetch(`/api/v2/context/kpi/${industryId}`)
      if (!res.ok) {
        throw new Error(`KPI context fetch failed: ${res.status}`)
      }
      const data: IndustryKPIContext = await res.json()
      kpiCacheRef.current[industryId] = data
      return data
    },
    [industryId]
  )

  const getIndustryNarrative = useCallback(
    async (opts?: { assetType?: string; role?: string }): Promise<IndustryNarrative> => {
      const cacheKey = `${industryId}|${opts?.assetType ?? ''}|${opts?.role ?? ''}`
      const cached = narrativeCacheRef.current[cacheKey]
      if (cached) return cached
      const params = new URLSearchParams()
      if (opts?.assetType) params.set('asset_type', opts.assetType)
      if (opts?.role) params.set('role', opts.role)
      const url = `/api/v2/context/narrative/${industryId}${
        params.toString() ? `?${params.toString()}` : ''
      }`
      const res = await fetch(url)
      if (!res.ok) {
        throw new Error(`Narrative fetch failed: ${res.status}`)
      }
      const data: IndustryNarrative = await res.json()
      narrativeCacheRef.current[cacheKey] = data
      return data
    },
    [industryId]
  )

  const whyCacheRef = useRef<Record<string, WhyThisMatters>>({})
  useEffect(() => { whyCacheRef.current = {} }, [industryId])

  const whyThisMatters = useCallback(
    async (opts: {
      metric: string
      role?: string
      observedValue?: number
    }): Promise<WhyThisMatters> => {
      const cacheKey = `${industryId}|${opts.metric}|${opts.role ?? ''}|${opts.observedValue ?? ''}`
      const cached = whyCacheRef.current[cacheKey]
      if (cached) return cached
      const params = new URLSearchParams({ metric: opts.metric })
      if (opts.role) params.set('role', opts.role)
      if (opts.observedValue !== undefined) {
        params.set('observed_value', String(opts.observedValue))
      }
      const res = await fetch(`/api/v2/context/why/${industryId}?${params.toString()}`)
      if (!res.ok) throw new Error(`whyThisMatters fetch failed: ${res.status}`)
      const data: WhyThisMatters = await res.json()
      whyCacheRef.current[cacheKey] = data
      return data
    },
    [industryId]
  )

  // ==========================================================================
  // CONTEXT VALUE
  // ==========================================================================

  const value: IndustryContextValue = useMemo(
    () => ({
      industry,
      industryId,
      setIndustry,
      availableIndustries,
      t,
      getAssetTypes,
      getAssetType,
      getKPIDomains,
      getKPIDomain,
      getMaintenanceTypes,
      getMaintenanceType,
      getComplianceStandards,
      // Professional role helpers
      getProfessionalRoles,
      getRolesByCategory,
      getProfessionalRole,
      getExplanationStyle,
      // Parts catalog helpers
      getPartsCatalog,
      getPart,
      getPartsByAssetType,
      getPartsByFailureMode,
      // Root cause helpers
      getRootCauseLibrary,
      getRootCause,
      getRootCausesByCategory,
      getRootCausePatterns,
      // Labor rate helpers
      getLaborRates,
      getLaborRate,
      // Narrative helpers
      generateNarrative,
      generateRoleBasedExplanation,
      // Module helpers
      getEnabledModules,
      getModuleDisplayName,
      getModuleSubPages,
      getModuleSubPageLabel,
      getModuleWorkflows,
      checkModuleEnabled,
      getNavItems,
      // Brain Intelligence context
      getPhysicsContext,
      getKPIContext,
      getIndustryNarrative,
      whyThisMatters,
      isLoading,
    }),
    [
      industry,
      industryId,
      setIndustry,
      t,
      getAssetTypes,
      getAssetType,
      getKPIDomains,
      getKPIDomain,
      getMaintenanceTypes,
      getMaintenanceType,
      getComplianceStandards,
      getProfessionalRoles,
      getRolesByCategory,
      getProfessionalRole,
      getExplanationStyle,
      getPartsCatalog,
      getPart,
      getPartsByAssetType,
      getPartsByFailureMode,
      getRootCauseLibrary,
      getRootCause,
      getRootCausesByCategory,
      getRootCausePatterns,
      getLaborRates,
      getLaborRate,
      generateNarrative,
      generateRoleBasedExplanation,
      getEnabledModules,
      getModuleDisplayName,
      getModuleSubPages,
      getModuleSubPageLabel,
      getModuleWorkflows,
      checkModuleEnabled,
      getNavItems,
      getPhysicsContext,
      getKPIContext,
      getIndustryNarrative,
      whyThisMatters,
      isLoading,
    ]
  )

  return <IndustryContext.Provider value={value}>{children}</IndustryContext.Provider>
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Hook to access industry context
 */
export function useIndustry(): IndustryContextValue {
  const context = useContext(IndustryContext)
  if (!context) {
    throw new Error('useIndustry must be used within an IndustryProvider')
  }
  return context
}

/**
 * Hook to get terminology translation function
 */
export function useTerminology(): (key: keyof IndustryTerminology) => string {
  const { t } = useIndustry()
  return t
}

/**
 * Hook to get asset types for current industry
 */
export function useAssetTypes(): AssetTypeConfig[] {
  const { getAssetTypes } = useIndustry()
  return getAssetTypes()
}

/**
 * Hook to get KPI domains for current industry
 */
export function useKPIDomains(): KPIDomainConfig[] {
  const { getKPIDomains } = useIndustry()
  return getKPIDomains()
}

/**
 * Hook to generate narrative from template
 */
export function useNarrative(
  templateId: string,
  data: Record<string, unknown>
): string {
  const { generateNarrative } = useIndustry()
  return useMemo(() => generateNarrative(templateId, data), [generateNarrative, templateId, data])
}

/**
 * Hook to get professional roles for current industry
 */
export function useProfessionalRoles(): ProfessionalRole[] {
  const { getProfessionalRoles } = useIndustry()
  return getProfessionalRoles()
}

/**
 * Hook to get roles by category
 */
export function useRolesByCategory(category: RoleCategory): ProfessionalRole[] {
  const { getRolesByCategory } = useIndustry()
  return useMemo(() => getRolesByCategory(category), [getRolesByCategory, category])
}

/**
 * Hook to get parts catalog
 */
export function usePartsCatalog(): PartsCatalog {
  const { getPartsCatalog } = useIndustry()
  return getPartsCatalog()
}

/**
 * Hook to get root cause library
 */
export function useRootCauseLibrary(): RootCauseLibrary {
  const { getRootCauseLibrary } = useIndustry()
  return getRootCauseLibrary()
}

/**
 * Hook to get labor rates
 */
export function useLaborRates(): LaborRateStructure {
  const { getLaborRates } = useIndustry()
  return getLaborRates()
}

/**
 * Hook to generate role-based explanation
 */
export function useRoleBasedExplanation(
  roleId: string,
  context: 'health' | 'anomaly' | 'prediction' | 'maintenance' | 'failure',
  data: Record<string, unknown>
): string {
  const { generateRoleBasedExplanation } = useIndustry()
  return useMemo(
    () => generateRoleBasedExplanation(roleId, context, data),
    [generateRoleBasedExplanation, roleId, context, data]
  )
}

// =============================================================================
// HOC FOR CLASS COMPONENTS
// =============================================================================

export interface WithIndustryProps {
  industry: IndustryConfig
  industryId: IndustryId
  t: (key: keyof IndustryTerminology) => string
}

export function withIndustry<P extends object>(
  WrappedComponent: React.ComponentType<P & WithIndustryProps>
): React.FC<Omit<P, keyof WithIndustryProps>> {
  const WithIndustryComponent: React.FC<Omit<P, keyof WithIndustryProps>> = props => {
    const { industry, industryId, t } = useIndustry()
    return <WrappedComponent {...(props as P)} industry={industry} industryId={industryId} t={t} />
  }
  WithIndustryComponent.displayName = `WithIndustry(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`
  return WithIndustryComponent
}

// =============================================================================
// EXPORTS
// =============================================================================

export { IndustryContext }
export type { IndustryProviderProps }
